/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import dayjs from "dayjs";
import { Else, If, Then } from "react-if";
import {
  Box,
  Button,
  FlexContainer,
  Message,
  Text,
  theme,
} from "@nordcloud/gnui";
import { BrickLoader, UniversalWrap } from "~/components";
import { dateFormat } from "~/constants";
import { useOrgCosts } from "~/views/home/hooks";
import { GlobalCosts } from "./GlobalCosts";

export function GlobalCostsWidget() {
  const {
    isCurrentBillingPeriod,
    activatePreviousBillingPeriod,
    showCurrentMonth,
    loading,
    error,
  } = useOrgCosts();
  return (
    <UniversalWrap
      errorProps={{ error }}
      loaderProps={{
        loading,
        Component: BrickLoader,
        viewBox: "0 0 10 1",
        backgroundColor: theme.color.background.ui01,
        style: { width: "100%", height: "auto" },
      }}
    >
      <If condition={showCurrentMonth}>
        <Then>
          <Box minHeight="136px">
            <GlobalCosts isCurrentBillingPeriod={isCurrentBillingPeriod} />
          </Box>
        </Then>
        <Else>
          <NoCosts
            onActivatePreviousBillingPeriod={activatePreviousBillingPeriod}
          />
        </Else>
      </If>
    </UniversalWrap>
  );
}

type NoCostsProps = {
  onActivatePreviousBillingPeriod: () => void;
};

function NoCosts({ onActivatePreviousBillingPeriod }: NoCostsProps) {
  return (
    <Message
      status="notification"
      image="info"
      css={{
        "> div:first-of-type": { marginBottom: 0 },
      }} // Fix for icon being too high
    >
      <FlexContainer direction="column" alignItems="start">
        <Text
          weight="medium"
          color={theme.color.text.info}
          mb={theme.spacing.spacing01}
        >
          We currently do not have any cost data for{" "}
          {dayjs().format(dateFormat.monthName)}.
        </Text>
        <Text mb={theme.spacing.spacing04} color={theme.color.text.text02}>
          The data should be available by the 3rd day of the month.
        </Text>
        <Button
          color="notification"
          severity="low"
          size="md"
          css={{
            textTransform: "inherit",
          }}
          onClick={onActivatePreviousBillingPeriod}
        >
          Switch to the Previous Month
        </Button>
      </FlexContainer>
    </Message>
  );
}
